type TestQuestion = {
  data?: unknown;
  guid?: string;
  learningObjectives?: unknown;
  question_guid?: string;
  ranking_scale?: number | null;
  [key: string]: unknown;
};

type Snapshot = {
  guid: string | undefined;
  question: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Según qué pantalla guardó la respuesta, `question` llega codificado una o dos veces (useQuizz hace
// JSON.stringify de un string), así que se parsea hasta que deja de ser string.
function parseUntilObject(value: unknown): unknown {
  let parsed = value;
  while (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  return parsed;
}

function hasLemonadeData(value: unknown): boolean {
  return isRecord(value) && isRecord(value.data);
}

function getAnswers(metadata: unknown): Array<Record<string, unknown> | null> {
  const parsed = parseUntilObject(metadata);
  // Formato antiguo: el array de respuestas en la raíz, sin envolver en `{ answers }`.
  const answers = Array.isArray(parsed) || !isRecord(parsed) ? parsed : parsed.answers;
  return Array.isArray(answers) ? answers.map((answer) => (isRecord(answer) ? answer : null)) : [];
}

function getQuestionGuid(question: TestQuestion | undefined): string | undefined {
  return question?.question_guid ?? question?.guid;
}

// Volver a guardar una pregunta en el backoffice cambia campos que no son su contenido (lemonadeVersion,
// ui_style, feedback, claves que el editor añade o quita): no cuentan como edición, o se usaría la copia y el
// tutor perdería los objetivos de aprendizaje de una pregunta que no ha cambiado.
const NOT_CONTENT_KEYS = new Set([
  'attachments',
  'feedback',
  'hints',
  'isEvaluable',
  'metadata',
  'showCheckIcon',
  'shuffle_options',
  'type',
  'ui_style',
  'validation',
]);
// De `validation` solo cuenta lo que decide la nota; `alt_responses` ausente equivale a vacío.
const SCORING_KEYS = ['alt_responses', 'penalty', 'penalty_mode', 'scoring_type', 'valid_response'];

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => [key, sortKeys(value[key])])
  );
}

function getContent(question: unknown): string {
  const data = isRecord(question) && isRecord(question.data) ? question.data : {};
  const validation = isRecord(data.validation) ? data.validation : {};
  const fields = Object.fromEntries(Object.entries(data).filter(([key]) => !NOT_CONTENT_KEYS.has(key)));
  const scoring = Object.fromEntries(SCORING_KEYS.map((key) => [key, validation[key] ?? (key === 'alt_responses' ? [] : null)]));
  return JSON.stringify(sortKeys({ ...fields, scoring }));
}

function hasSameContent(snapshot: unknown, current: TestQuestion | undefined): boolean {
  return !!current && getContent(snapshot) === getContent(parseUntilObject(current.data));
}

function hasUsableCopy({ guid, question }: Snapshot): boolean {
  return !!guid && hasLemonadeData(question);
}

function fromCopy({ guid, question }: Snapshot, current: TestQuestion | undefined): TestQuestion {
  const data = JSON.stringify(question);
  if (current) {
    // Los objetivos de aprendizaje del CMS son los de la pregunta editada, no los de la que respondió el alumno.
    const copy: TestQuestion = { ...current, question_guid: guid, data };
    delete copy.learningObjectives;
    return copy;
  }
  // Pregunta borrada del CMS: su máximo ya no existe en ningún sitio, y uno inventado daría una nota falsa.
  // `ranking_scale: null` hace que QuestionItem pinte solo los puntos obtenidos.
  return { question_guid: guid, data, ranking_scale: null };
}

/**
 * Preguntas tal y como las vio el alumno, alineadas con sus respuestas: la pregunta `i` es la de la respuesta
 * `i` de `job.metadata`, que es como las emparejan todas las pantallas. Para jobs sin freeze cuyo test se editó,
 * se reordenó o perdió preguntas después de responder (TANAI-2575). Si cuadra con el CMS, devuelve
 * `questions` sin tocar.
 */
export function getQuestionsAnsweredInJob(metadata: unknown, questions: TestQuestion[] | undefined): TestQuestion[] | undefined {
  // Sin preguntas del CMS (la petición falló) no se reconstruye: faltarían los máximos de todas.
  if (!Array.isArray(questions) || questions.length === 0) return questions;
  const answers = getAnswers(metadata);
  if (answers.length === 0) return questions;

  const snapshots: Snapshot[] = answers.map((answer) => ({
    guid: typeof answer?.questionGuid === 'string' ? answer.questionGuid : undefined,
    question: parseUntilObject(answer?.question),
  }));
  const currentByGuid = new Map(questions.map((question) => [getQuestionGuid(question), question]));
  const needsCopy = (snapshot: Snapshot): boolean => hasUsableCopy(snapshot) && !hasSameContent(snapshot.question, currentByGuid.get(snapshot.guid));
  // Un reordenado o una pregunta insertada en el CMS descuadra el emparejamiento por posición igual que una edición.
  const isAligned = snapshots.every(({ guid }, index) => !guid || getQuestionGuid(questions[index]) === guid);
  if (isAligned && !snapshots.some(needsCopy)) return questions;

  const answeredGuids = new Set(snapshots.map(({ guid }) => guid).filter(Boolean));
  const unanswered = questions.filter((question) => !answeredGuids.has(getQuestionGuid(question)));

  // Una respuesta sin pregunta propia (sin GUID, o de una pregunta borrada sin copia) ocupa su hueco con la
  // pregunta del CMS de esa posición si nadie la respondió, o con la primera que nadie respondió.
  function takeUnanswered(index: number): TestQuestion | undefined {
    const atIndex = questions[index];
    const question = atIndex && unanswered.includes(atIndex) ? atIndex : unanswered[0];
    if (question) unanswered.splice(unanswered.indexOf(question), 1);
    return question;
  }

  const answeredQuestions = snapshots.map((snapshot, index) => {
    const current = currentByGuid.get(snapshot.guid);
    if (needsCopy(snapshot)) return fromCopy(snapshot, current);
    return current ?? takeUnanswered(index);
  });
  // Un hueco sin nada con qué rellenarlo no se puede quitar sin que la pregunta siguiente herede su respuesta:
  // en ese caso el test se deja como viene del CMS.
  if (answeredQuestions.some((question) => !question)) return questions;

  return [...answeredQuestions, ...unanswered];
}
